import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import PaymentButton from '../../components/PaymentButton';
import BackNavBar from '../../components/BackNavBar';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import getTenders from '../../utils/getTenders';
import { Pages } from '../../App';
import { setEwalletAvailableData, setStfAvailableData, updateSTFData } from '../../DumbleData';
import { ConfigContext } from '../../providers/ConfigContext';
import BigNumber from 'bignumber.js';

interface Props {
	amountDue: BigNumber;
	setBookfairInfo: any;
	stfBalance: BigNumber;
	setDefaultConfirmationEmail: any;
	chairpersonEmail: string;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const [acquireTenders] = getTenders();

const PaymentMethodContent = ({ amountDue, stfBalance, setBookfairInfo, setDefaultConfirmationEmail, chairpersonEmail, setCurrentPage }: Props) => {
	const { apiBase } = useContext(ConfigContext);
	const [isLoading, setIsLoading] = useState(true);

	const [eWalletDisabled, setEWalletDisabled] = useState(false);
	const [shareTheFairDisabled, setShareTheFairDisabled] = useState(false);

	useEffect(() => {
		const getTenderInfo = async () => {
			setIsLoading(true);
			const { data, error } = await acquireTenders(apiBase);
			if (error) {
				console.error(error);
				setCurrentPage(Pages.Error);
			} else if (data != null) {
				setEWalletDisabled(!data.ewalletEnabled);
				const newStfBalance = BigNumber(data.stfBalance);
				const stfDisabled = newStfBalance.lt(amountDue) || newStfBalance.isZero();
				setShareTheFairDisabled(stfDisabled);
				setStfAvailableData(!stfDisabled);
				updateSTFData(data.stfBalance);
				setEwalletAvailableData(data.ewalletEnabled);
				setBookfairInfo((prevState: any) => ({
					...prevState,
					stfBalance: newStfBalance,
				}));
			}
			setIsLoading(false);
		};

		getTenderInfo();
	}, [amountDue.toNumber(), apiBase, setBookfairInfo, setCurrentPage]);

	const handleCreditCardClick = () => {
		const method = 'Credit Card';
		handleSatellite(method);
		setDefaultConfirmationEmail(chairpersonEmail);
		setCurrentPage(Pages.CreditCardForm);
	};
	const handleEWalletClick = () => {
		const method = 'eWallet';
		handleSatellite(method);
		setCurrentPage(Pages.eWallet);
	};
	const handleShareTheFairClick = () => {
		const method = 'Share the Fair';
		handleSatellite(method);
		setDefaultConfirmationEmail(chairpersonEmail);
		setCurrentPage(Pages.STFRedeem);
	};
	const handleCheckClick = () => {
		const method = 'Check';
		handleSatellite(method);
		setDefaultConfirmationEmail(chairpersonEmail);
		setCurrentPage(Pages.Checks);
	};
	const handleCashClick = () => {
		const method = 'Cash';
		handleSatellite(method);
		setDefaultConfirmationEmail(chairpersonEmail);
		setCurrentPage(Pages.CashPayment);
	};
	const handleSDClick = () => {
		const method = 'Scholastic Dollars';
		handleSatellite(method);
		setDefaultConfirmationEmail(chairpersonEmail);
		setCurrentPage(Pages.ScholasticDollars);
	};
	const handlePOClick = () => {
		const method = 'Purchase Order';
		handleSatellite(method);
		setDefaultConfirmationEmail(chairpersonEmail);
		setCurrentPage(Pages.POPayment);
	};

	const handleSatellite = (paymentMethodSelected: string) => {
		window._satellite.track('new-purchase-payment-method-selection', { paymentMethod: paymentMethodSelected });
	};

	return (
		<>
			{!isLoading && (
				<>
					<BackNavBar setCurrentPage={setCurrentPage} title="Payment Method" location={Pages.NewPurchase} />
					<PaymentMethodPage>
						<PageWrapper>
							<PaymentWrapper>
								<PaymentButton onClick={handleCreditCardClick}>
									<InnerText>Credit Card</InnerText>
								</PaymentButton>
								<PaymentButton disabled={eWalletDisabled} onClick={handleEWalletClick}>
									<InnerText>eWallet</InnerText>
								</PaymentButton>
								<PaymentButton onClick={handleCashClick}>
									<InnerText>Cash</InnerText>
								</PaymentButton>
								<PaymentButton onClick={handleCheckClick}>
									<InnerText>Check</InnerText>
								</PaymentButton>
								<PaymentButton onClick={handleSDClick}>
									<InnerText>Scholastic Dollars</InnerText>
								</PaymentButton>
								<PaymentButton disabled={shareTheFairDisabled} onClick={handleShareTheFairClick}>
									<ShareTheFairDiv>
										<InnerText>Share the Fair</InnerText>
										<FineInnerText>Balance: {formatCurrencyNumber(stfBalance.toNumber())}</FineInnerText>
									</ShareTheFairDiv>
								</PaymentButton>
								<PaymentButton onClick={handlePOClick}>
									<CenteredInnerText>Purchase Order</CenteredInnerText>
								</PaymentButton>
							</PaymentWrapper>
							{shareTheFairDisabled && <ErrorText>The balance of your Share the Fair account cannot cover the total amount required for payment.</ErrorText>}
						</PageWrapper>
					</PaymentMethodPage>
				</>
			)}
		</>
	);
};

const PaymentMethodPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 20px;
	margin: 32px 0 32px 0;
	width: 580px;

	@media (max-width: 720px) {
		width: 280px;
	}
`;

const PaymentWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(280px, 1fr));
	gap: 20px;
	width: 100%;

	& > :last-child {
		justify-self: center;
		grid-column: span 2;
	}
	@media (max-width: 720px) {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}
`;

const InnerText = styled.p`
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 19px;
	color: #e81111;
	margin: 10px 0 10px 0;
	text-align: left;
`;

const CenteredInnerText = styled(InnerText)`
	text-align: center;
	width: 100%;
	@media (max-width: 720px) {
		text-align: left;
	}
`;

const FineInnerText = styled.p`
	font-size: 13px;
	font-style: normal;
	font-weight: 500;
	line-height: 15px;
	color: #333333;
	margin: 0;
`;

const ShareTheFairDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 6px;
	flex: 1 0 0;
	${InnerText} {
		margin: 0px;
	}
`;

const ErrorText = styled.p`
	font-size: 13px;
	font-style: normal;
	font-weight: 500;
	line-height: 15px;
	color: #333333;
	margin: 0;
`;

export default PaymentMethodContent;
